import { CheckCircle2, Circle, Loader2, Play, SquarePen } from 'lucide-react'
import type React from 'react'
import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import styled from 'styled-components'
import { useAthletes } from '../../contexts/AthletesContext'
import { useAuth } from '../../contexts/AuthContext'
import { supabase } from '../../lib/supabase'
import { theme } from '../../theme/theme'
import type { ScheduledTask } from '../../types/database.tables'
import { Button } from '../common/Button'
import { type TaskExecutionResult, TaskExecutionResultsModal } from '../common/TaskExecutionResultsModal'

type Periodicity = 'after_workout' | 'daily' | 'weekly' | 'monthly' | 'yearly'

/**
 * Calculate the next execution time based on periodicity and schedule parameters
 */
export function calculateNextExecution(
  periodicity: string,
  scheduleHour: number | null = 9,
  scheduleDayOfWeek: number | null = null,
  scheduleDayOfMonth: number | null = null,
  baseTime: Date = new Date()
): Date | null {
  const now = new Date(baseTime)
  const hour = scheduleHour ?? 9

  // Start with today at the scheduled hour
  const nextExecution = new Date(now)
  nextExecution.setHours(hour, 0, 0, 0)

  switch (periodicity) {
    case 'after_workout':
      // For after_workout, next execution is not scheduled
      return null

    case 'daily':
      // If today's time has passed, move to tomorrow
      if (nextExecution <= now) {
        nextExecution.setDate(nextExecution.getDate() + 1)
      }
      break

    case 'weekly': {
      const targetDay = scheduleDayOfWeek ?? 0
      const currentDay = nextExecution.getDay()
      // Convert Sunday (0) to 6, and Monday-Saturday to 0-5
      const currentDayAdjusted = currentDay === 0 ? 6 : currentDay - 1
      const targetDayAdjusted = targetDay

      let daysUntilTarget = targetDayAdjusted - currentDayAdjusted
      if (daysUntilTarget < 0 || (daysUntilTarget === 0 && nextExecution <= now)) {
        daysUntilTarget += 7
      }
      nextExecution.setDate(nextExecution.getDate() + daysUntilTarget)
      break
    }

    case 'monthly': {
      const targetDay = scheduleDayOfMonth ?? 1
      if (targetDay === -1) {
        // Last day of month
        nextExecution.setMonth(nextExecution.getMonth() + 1, 0) // Sets to last day of current month
        if (nextExecution <= now) {
          nextExecution.setMonth(nextExecution.getMonth() + 2, 0) // Move to last day of next month
        }
      } else {
        // First day of month (or specific day)
        nextExecution.setDate(targetDay)
        if (nextExecution <= now) {
          nextExecution.setMonth(nextExecution.getMonth() + 1, targetDay)
        }
      }
      break
    }

    case 'yearly':
      if (nextExecution <= now) {
        nextExecution.setFullYear(nextExecution.getFullYear() + 1)
      }
      break

    default:
      // Default to daily
      if (nextExecution <= now) {
        nextExecution.setDate(nextExecution.getDate() + 1)
      }
  }

  return nextExecution
}

const Container = styled.div`
  height: 100%;
  display: flex;
  overflow: hidden;
  background-color: ${({ theme }) => theme.colors.background};

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`

const TaskList = styled.div`
  background-color: ${({ theme }) => theme.colors.cardBackground};
  border-right: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  flex-basis: 300px;
  flex-direction: column;
`

const TaskListScroll = styled.div`
  flex: 1;
  overflow-y: auto;
`

const SidebarHeader = styled.div`
  padding: ${({ theme }) => theme.spacing.sm};
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const SidebarTitle = styled.h2`
  margin-left: ${({ theme }) => theme.spacing.sm};
  font-size: ${({ theme }) => theme.typography.sm};
  color: ${({ theme }) => theme.colors.textMuted};
`

const TaskItem = styled.div<{ $isActive: boolean }>`
  padding: ${({ theme }) => theme.spacing.md};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  cursor: pointer;
  background-color: ${({ theme, $isActive }) => ($isActive ? theme.colors.surfaceHover : 'transparent')};
  transition: ${({ theme }) => theme.transitions.default};

  &:hover {
    background-color: ${({ theme }) => theme.colors.surfaceHover};
  }

  h4 {
    margin: 0;
    font-size: ${({ theme }) => theme.typography.sm};
    font-weight: 400;
    color: ${({ theme }) => theme.colors.text};
    flex: 1;
  }
`

const TaskHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`

const TaskMeta = styled.div`
  font-size: ${({ theme }) => theme.typography.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-left: 28px; /* Align with title after icon */
`

const StatusIcon = styled.div<{ $isActive: boolean }>`
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  color: ${({ theme, $isActive }) => ($isActive ? theme.colors.primary : theme.colors.secondaryLight)}
`

const DetailPanel = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`

const DetailScroll = styled.div`
  flex: 1;
  padding: ${({ theme }) => theme.spacing.lg};
  overflow-y: auto;

  @media (max-width: 640px) {
    padding: ${({ theme }) => theme.spacing.sm};
  }
`

const FormGroup = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.lg};

  label {
    display: block;
    margin-bottom: ${({ theme }) => theme.spacing.xs};
    font-size: ${({ theme }) => theme.typography.sm};
    font-weight: 500;
    color: ${({ theme }) => theme.colors.textMuted};
  }

  input, select, textarea {
    width: 100%;
    padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
    border: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: ${({ theme }) => theme.borderRadius.sm};
    font-size: ${({ theme }) => theme.typography.md};
    background-color: ${({ theme }) => theme.colors.surface};
    color: ${({ theme }) => theme.colors.text};
    font-family: inherit;

    &:focus {
      outline: none;
      border-color: ${({ theme }) => theme.colors.primary};
    }
  }
    
  input.execution-date {
    font-size: ${({ theme }) => theme.typography.md};
    color: ${({ theme }) => theme.colors.text};
    background-color: ${({ theme }) => theme.colors.cardBackground};
    border: none;
  }

  textarea {
    resize: vertical;
    min-height: 120px;
  }
`

const InlineFieldGroup = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  align-items: center;

  .field-label {
    font-size: ${({ theme }) => theme.typography.md};
    color: ${({ theme }) => theme.colors.textSecondary};
    white-space: nowrap;
  }

  select {
    padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
    border: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: ${({ theme }) => theme.borderRadius.sm};
    font-size: ${({ theme }) => theme.typography.md};
    background-color: ${({ theme }) => theme.colors.surface};
    color: ${({ theme }) => theme.colors.text};

    &:focus {
      outline: none;
      border-color: ${({ theme }) => theme.colors.primary};
    }
  }
`

const CheckboxGroup = styled.div`
  display: flex;
  width: fit-content;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  font-size: ${({ theme }) => theme.typography.md};
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  padding: ${({ theme }) => theme.spacing.sm};

  input[type="checkbox"] {
    width: auto;
    accent-color: ${({ theme }) => theme.colors.primary};
    margin: 0 0 0 ${({ theme }) => theme.spacing.xs}
  }
  label {
    margin: 0 0 0 ${({ theme }) => theme.spacing.xs}
  }
`

const AthleteSelectionContainer = styled.div`
  display: flex;
  margin-top: ${({ theme }) => theme.spacing.sm};
`

const AthleteCheckbox = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.xs} 0;
  margin-right: ${({ theme }) => theme.spacing.sm};
  font-size: ${({ theme }) => theme.typography.sm};
  color: ${({ theme }) => theme.colors.text};

  label {
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing.sm};
    cursor: pointer;
    flex: 1;
  }

  input[type="checkbox"] {
    width: auto;
    margin: 0;
  }

    /*
  .execute-button {
    opacity: 0;
    transition: opacity 0.2s;
    padding: 4px;
    background: transparent;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    color: ${({ theme }) => theme.colors.primary};

    &:hover:not(:disabled) {
      color: ${({ theme }) => theme.colors.primaryDark};
    }

    &:disabled {
      cursor: not-allowed;
      color: ${({ theme }) => theme.colors.textMuted};
      opacity: 0.5;
    }
  }

  &:hover .execute-button {
    opacity: 1;
  }
     */
`

const ButtonGroup = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  justify-content: flex-end;
  margin-top: ${({ theme }) => theme.spacing.lg};
`

const EmptyState = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.textSecondary};
  text-align: start;
  padding: ${({ theme }) => theme.spacing.xl};

  p {
    font-size: ${({ theme }) => theme.typography.md};
    margin: ${({ theme }) => theme.spacing.sm} 0;
  }
`

const LoadingState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.xl};
  color: ${({ theme }) => theme.colors.textSecondary};
`

const Notification = styled.div<{ $show: boolean }>`
  position: fixed;
  top: 70px;
  right: ${({ theme }) => theme.spacing.lg};
  background-color: ${({ theme }) => theme.colors.success || '#4caf50'};
  color: white;
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  transform: translateX(${({ $show }) => ($show ? '0' : '400px')});
  opacity: ${({ $show }) => ($show ? '1' : '0')};
  transition: all 0.3s ease-in-out;
  font-size: ${({ theme }) => theme.typography.md};
  font-weight: 500;

  @media (max-width: 640px) {
    right: ${({ theme }) => theme.spacing.sm};
    left: ${({ theme }) => theme.spacing.sm};
  }
`

export const AutomationView: React.FC = () => {
  const { user } = useAuth()
  const { athletes: athletesData } = useAthletes()
  const [tasks, setTasks] = useState<ScheduledTask[]>([])
  const [selectedTask, setSelectedTask] = useState<ScheduledTask | null>(null)
  const [originalTask, setOriginalTask] = useState<ScheduledTask | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isExecuting, setIsExecuting] = useState(false)
  const titleInputRef = useRef<HTMLInputElement>(null)
  const [isResultsModalOpen, setIsResultsModalOpen] = useState(false)
  const [executionResults, setExecutionResults] = useState<TaskExecutionResult[] | null>(null)
  const navigate = useNavigate()
  const { automationId } = useParams<{ automationId: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const [showNotification, setShowNotification] = useState(false)
  const [notificationMessage, setNotificationMessage] = useState('')

  // Transform athletes data to the format needed by AutomationView
  const athletes = athletesData.map((athlete) => ({
    id: athlete.id,
    name: athlete.name,
  }))

  // Check if the current task has unsaved changes
  const isTaskDirty = (): boolean => {
    if (!selectedTask || !originalTask) return false
    if (selectedTask.id === 'new') return true // New tasks are always dirty

    // Compare relevant fields
    return (
      selectedTask.title !== originalTask.title ||
      selectedTask.description !== originalTask.description ||
      selectedTask.prompt !== originalTask.prompt ||
      selectedTask.periodicity !== originalTask.periodicity ||
      selectedTask.is_active !== originalTask.is_active ||
      selectedTask.email_results !== originalTask.email_results ||
      selectedTask.email_mode !== originalTask.email_mode ||
      selectedTask.schedule_hour !== originalTask.schedule_hour ||
      selectedTask.schedule_day_of_week !== originalTask.schedule_day_of_week ||
      selectedTask.schedule_day_of_month !== originalTask.schedule_day_of_month ||
      JSON.stringify(selectedTask.target_athlete_ids) !== JSON.stringify(originalTask.target_athlete_ids)
    )
  }

  const formatPeriodicity = (periodicity: string): string => {
    return periodicity.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  }

  const formatTimeInUserTimezone = (hour: number): string => {
    const now = new Date()
    now.setHours(hour, 0, 0, 0)
    return now.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
  }

  const formatDayOfWeek = (dayOfWeek: number): string => {
    const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
    return days[dayOfWeek]
  }

  const getTimezoneOffset = (): string => {
    const offset = -new Date().getTimezoneOffset()
    const hours = Math.floor(Math.abs(offset) / 60)
    const minutes = Math.abs(offset) % 60
    const sign = offset >= 0 ? '+' : '-'
    return `UTC${sign}${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
  }

  const formatAthleteTarget = (targetAthleteIds: string[] | null): string => {
    if (!targetAthleteIds || targetAthleteIds.length === 0) {
      return 'No athletes'
    }
    if (targetAthleteIds.length === 1) {
      const athlete = athletes.find((a) => a.id === targetAthleteIds[0])
      return athlete ? athlete.name : '1 athlete'
    }
    return `${targetAthleteIds.length} athletes`
  }

  const formatNextExecution = (nextExecutionAt: string | null): string => {
    if (!nextExecutionAt) {
      return 'Not scheduled'
    }

    const date = new Date(nextExecutionAt)
    const now = new Date()

    // Check if it's in the past
    if (date < now) {
      return 'Overdue - will run when scheduler next checks'
    }

    // Create date objects with time set to midnight for comparison
    const dateAtMidnight = new Date(date)
    dateAtMidnight.setHours(0, 0, 0, 0)

    const todayAtMidnight = new Date(now)
    todayAtMidnight.setHours(0, 0, 0, 0)

    const tomorrowAtMidnight = new Date(todayAtMidnight)
    tomorrowAtMidnight.setDate(tomorrowAtMidnight.getDate() + 1)

    const timeStr = date.toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
      hour12: false,
    })

    // Check if it's today
    if (dateAtMidnight.getTime() === todayAtMidnight.getTime()) {
      return `Today at ${timeStr}`
    }

    // Check if it's tomorrow
    if (dateAtMidnight.getTime() === tomorrowAtMidnight.getTime()) {
      return `Tomorrow at ${timeStr}`
    }

    // Format as: "Mon, Oct 28, 2025 at 14:30" for later dates
    const dateStr = date.toLocaleDateString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })

    return `${dateStr} at ${timeStr}`
  }

  const formatLastExecution = (lastExecutedAt: string | null): string => {
    if (!lastExecutedAt) {
      return 'Never executed'
    }

    const date = new Date(lastExecutedAt)
    const now = new Date()

    // Create date objects with time set to midnight for comparison
    const dateAtMidnight = new Date(date)
    dateAtMidnight.setHours(0, 0, 0, 0)

    const todayAtMidnight = new Date(now)
    todayAtMidnight.setHours(0, 0, 0, 0)

    const yesterdayAtMidnight = new Date(todayAtMidnight)
    yesterdayAtMidnight.setDate(yesterdayAtMidnight.getDate() - 1)

    const timeStr = date.toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
      hour12: false,
    })

    // Check if it's today
    if (dateAtMidnight.getTime() === todayAtMidnight.getTime()) {
      return `Today at ${timeStr}`
    }

    // Check if it's yesterday
    if (dateAtMidnight.getTime() === yesterdayAtMidnight.getTime()) {
      return `Yesterday at ${timeStr}`
    }

    // Format as: "Mon, Oct 28, 2025 at 14:30" for older dates
    const dateStr = date.toLocaleDateString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })

    return `${dateStr} at ${timeStr}`
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    loadTasks()
  }, [user])

  // Handle URL parameter for automation selection
  useEffect(() => {
    if (automationId && tasks.length > 0) {
      const task = tasks.find(t => t.id === automationId)
      if (task) {
        setSelectedTask(task)
        setOriginalTask(JSON.parse(JSON.stringify(task)))
      }
    }
  }, [automationId, tasks])

  // Handle unsubscribe action
  useEffect(() => {
    const handleUnsubscribe = async () => {
      const unsubscribe = searchParams.get('unsubscribe')
      if (unsubscribe === 'true' && automationId && selectedTask && selectedTask.id === automationId) {
        // Deactivate the automation (only if it's currently active)
        if (!selectedTask.is_active) {
          // Already inactive, just remove the parameter and show notification
          searchParams.delete('unsubscribe')
          setSearchParams(searchParams, { replace: true })
          setNotificationMessage('This automation is already inactive')
          setShowNotification(true)
          setTimeout(() => {
            setShowNotification(false)
          }, 5000)
          return
        }

        try {
          const { error } = await supabase
            .from('scheduled_tasks')
            .update({ is_active: false })
            .eq('id', automationId)

          if (error) throw error

          // Update the selected task state
          const updatedTask = { ...selectedTask, is_active: false }
          setSelectedTask(updatedTask)
          setOriginalTask(JSON.parse(JSON.stringify(updatedTask)))

          // Reload tasks to update the list
          await loadTasks()

          // Show notification
          setNotificationMessage('Automation has been deactivated successfully')
          setShowNotification(true)

          // Remove the unsubscribe parameter from URL
          searchParams.delete('unsubscribe')
          setSearchParams(searchParams, { replace: true })

          // Hide notification after 5 seconds
          setTimeout(() => {
            setShowNotification(false)
          }, 5000)
        } catch (error) {
          console.error('Error deactivating automation:', error)
          setNotificationMessage('Failed to deactivate automation')
          setShowNotification(true)
          setTimeout(() => {
            setShowNotification(false)
          }, 5000)
        }
      }
    }

    if (automationId && selectedTask && searchParams.get('unsubscribe') === 'true') {
      handleUnsubscribe()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [automationId, selectedTask?.id, searchParams.get('unsubscribe')])

  const loadTasks = async () => {
    if (!user) return

    setIsLoading(true)
    try {
      const { data, error } = await supabase
        .from('scheduled_tasks')
        .select('*')
        .eq('user_id', user.id)
        .order('description', { ascending: true })

      if (error) throw error
      setTasks(data || [])
    } catch (error) {
      console.error('Error loading tasks:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const createNewTask = () => {
    if (!user) return

    const nextExecution = calculateNextExecution('weekly', 9, 0, null)
    const newTask: ScheduledTask = {
      id: 'new',
      user_id: user.id,
      title: '',
      description: '',
      prompt: '',
      periodicity: 'weekly',
      is_active: true,
      email_results: true,
      email_mode: 'always',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      last_executed_at: null,
      last_execution_error: null,
      next_execution_at: nextExecution ? nextExecution.toISOString() : null,
      schedule_hour: 9,
      schedule_day_of_week: 0,
      schedule_day_of_month: null,
      target_athlete_ids: null,
    }
    setSelectedTask(newTask)
    setOriginalTask(null) // New task has no original
    navigate('/automations/new')

    // Focus the title input after the state update
    setTimeout(() => {
      titleInputRef.current?.focus()
    }, 0)
  }

  const saveTask = async () => {
    if (!selectedTask || !user) return

    setIsLoading(true)
    try {
      if (selectedTask.id === 'new') {
        // Create new task
        const { data, error } = await supabase
          .from('scheduled_tasks')
          .insert({
            user_id: user.id,
            title: selectedTask.title,
            description: selectedTask.description,
            prompt: selectedTask.prompt,
            periodicity: selectedTask.periodicity,
            is_active: selectedTask.is_active,
            email_results: selectedTask.email_results,
            email_mode: selectedTask.email_mode,
            schedule_hour: selectedTask.schedule_hour,
            schedule_day_of_week: selectedTask.schedule_day_of_week,
            schedule_day_of_month: selectedTask.schedule_day_of_month,
            target_athlete_ids: selectedTask.target_athlete_ids,
            next_execution_at: selectedTask.next_execution_at,
          })
          .select()
        if (error) throw error
        if (!data || data.length === 0) throw new Error('No data returned from insert')
        const newTaskId = data[0].id
        selectedTask.id = newTaskId // update the task ID
        setSelectedTask(selectedTask)
        navigate(`/automations/${newTaskId}`)
      } else {
        // Update existing task
        const { error } = await supabase
          .from('scheduled_tasks')
          .update({
            title: selectedTask.title,
            description: selectedTask.description,
            prompt: selectedTask.prompt,
            periodicity: selectedTask.periodicity,
            is_active: selectedTask.is_active,
            email_results: selectedTask.email_results,
            email_mode: selectedTask.email_mode,
            schedule_hour: selectedTask.schedule_hour,
            schedule_day_of_week: selectedTask.schedule_day_of_week,
            schedule_day_of_month: selectedTask.schedule_day_of_month,
            target_athlete_ids: selectedTask.target_athlete_ids,
            next_execution_at: selectedTask.next_execution_at,
          })
          .eq('id', selectedTask.id)

        if (error) throw error
      }

      await loadTasks()
      // Update originalTask to match the saved state
      if (selectedTask) {
        setOriginalTask(JSON.parse(JSON.stringify(selectedTask)))
      }
    } catch (error) {
      console.error('Error saving task:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const deleteTask = async (taskId: string) => {
    if (!user) return

    setIsLoading(true)
    try {
      const { error } = await supabase.from('scheduled_tasks').delete().eq('id', taskId)

      if (error) throw error

      setSelectedTask(null)
      setOriginalTask(null)
      navigate('/automations')
      await loadTasks()
    } catch (error) {
      console.error('Error deleting task:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const executeTaskNow = async (task: ScheduledTask, athleteId: string | null) => {
    if (!user) return

    setIsExecuting(true)
    try {
      console.log('Execute task now:', { taskId: task.id, athleteId })
      const { data, error } = await supabase.functions.invoke('task-scheduler', {
        body: {
          task_id: task.id,
          athlete_id: athleteId,
          is_manual: true,
        },
      })

      if (error) {
        console.error('Failed to execute task:', error)
        alert(`Failed to execute task: ${error.message}`)
        return
      }

      // Show results in modal
      setExecutionResults(data)
      setIsResultsModalOpen(true)
    } catch (error) {
      console.error('Error executing task:', error)
      alert('An error occurred while executing the task')
    } finally {
      setIsExecuting(false)
    }
  }

  if (isLoading && tasks.length === 0) {
    return (
      <Container>
        <LoadingState>Loading tasks...</LoadingState>
      </Container>
    )
  }

  return (
    <Container>
      <TaskList>
        <SidebarHeader>
          <Button
            variant="default"
            size="md"
            style={{ width: '100%', textAlign: 'start' }}
            onClick={createNewTask}
            disabled={isLoading}
          >
            <SquarePen size={16} />
            New Automation
          </Button>
        </SidebarHeader>
        <SidebarTitle>Automations</SidebarTitle>
        <TaskListScroll
          onClick={() => {
            setSelectedTask(null)
            setOriginalTask(null)
            navigate('/automations')
          }}
        >
          {tasks.length === 0 ? (
            <EmptyState>
              <p>No saved automations yet.</p>
            </EmptyState>
          ) : (
            tasks.map((task) => (
              <TaskItem
                key={task.id}
                $isActive={selectedTask?.id === task.id}
                onClick={(e) => {
                  setSelectedTask(task)
                  setOriginalTask(JSON.parse(JSON.stringify(task))) // Deep copy for comparison
                  navigate(`/automations/${task.id}`)
                  e.stopPropagation()
                }}
              >
                <TaskHeader>
                  <StatusIcon $isActive={task.is_active ?? false}>
                    {task.is_active ? <CheckCircle2 size={20} /> : <Circle size={20} />}
                  </StatusIcon>
                  <h4>{task.title}</h4>
                </TaskHeader>
                <TaskMeta>
                  {formatPeriodicity(task.periodicity)}
                  {task.schedule_day_of_week !== null && ` on  ${formatDayOfWeek(task.schedule_day_of_week)}`}
                  {task.schedule_hour !== null && ` at ${formatTimeInUserTimezone(task.schedule_hour)}`}
                  {' • '}
                  {formatAthleteTarget(task.target_athlete_ids)}
                </TaskMeta>
              </TaskItem>
            ))
          )}
        </TaskListScroll>
      </TaskList>

      <DetailPanel>
        {selectedTask ? (
          <DetailScroll>
            <FormGroup>
              <label htmlFor="title">Name *</label>
              <div style={{ display: 'flex', flexDirection: 'row', gap: '10px' }}>
                <input
                  ref={titleInputRef}
                  type="text"
                  name="title"
                  value={selectedTask.title}
                  onChange={(e) => setSelectedTask({ ...selectedTask, title: e.target.value })}
                  placeholder="Name your automation"
                />
                <CheckboxGroup>
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={selectedTask.is_active ?? false}
                    onChange={(e) => setSelectedTask({ ...selectedTask, is_active: e.target.checked })}
                  />
                  <label htmlFor="is_active">Active</label>
                </CheckboxGroup>
              </div>
            </FormGroup>

            <FormGroup>
              <label htmlFor="periodicity">This automation is executed</label>
              <InlineFieldGroup>
                <select
                  value={selectedTask.periodicity}
                  name="periodicity"
                  onChange={(e) => {
                    const newPeriodicity = e.target.value as Periodicity
                    const scheduleHour = newPeriodicity !== 'after_workout' ? selectedTask.schedule_hour || 9 : null
                    const scheduleDayOfWeek =
                      newPeriodicity === 'weekly' ? selectedTask.schedule_day_of_week || 0 : null
                    const scheduleDayOfMonth =
                      newPeriodicity === 'monthly' ? selectedTask.schedule_day_of_month || 1 : null

                    const nextExecution = calculateNextExecution(
                      newPeriodicity,
                      scheduleHour,
                      scheduleDayOfWeek,
                      scheduleDayOfMonth
                    )
                    const nextExecutionAt = nextExecution ? nextExecution.toISOString() : null

                    setSelectedTask({
                      ...selectedTask,
                      periodicity: newPeriodicity,
                      schedule_hour: scheduleHour,
                      schedule_day_of_week: scheduleDayOfWeek,
                      schedule_day_of_month: scheduleDayOfMonth,
                      next_execution_at: nextExecutionAt,
                    })
                  }}
                >
                  <option value="after_workout">After workout upload</option>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>

                {/* Day selection for weekly tasks */}
                {selectedTask.periodicity === 'weekly' && (
                  <>
                    <span className="field-label">on</span>
                    <select
                      value={selectedTask.schedule_day_of_week ?? 1}
                      onChange={(e) => {
                        const newDayOfWeek = Number(e.target.value)
                        const nextExecution = calculateNextExecution(
                          selectedTask.periodicity,
                          selectedTask.schedule_hour,
                          newDayOfWeek,
                          selectedTask.schedule_day_of_month
                        )
                        const nextExecutionAt = nextExecution ? nextExecution.toISOString() : null

                        setSelectedTask({
                          ...selectedTask,
                          schedule_day_of_week: newDayOfWeek,
                          next_execution_at: nextExecutionAt,
                        })
                      }}
                    >
                      <option value={0}>Monday</option>
                      <option value={1}>Tuesday</option>
                      <option value={2}>Wednesday</option>
                      <option value={3}>Thursday</option>
                      <option value={4}>Friday</option>
                      <option value={5}>Saturday</option>
                      <option value={6}>Sunday</option>
                    </select>
                  </>
                )}

                {/* Day selection for monthly tasks */}
                {selectedTask.periodicity === 'monthly' && (
                  <>
                    <span className="field-label">on</span>
                    <select
                      value={selectedTask.schedule_day_of_month || 1}
                      onChange={(e) => {
                        const newDayOfMonth = Number(e.target.value)
                        const nextExecution = calculateNextExecution(
                          selectedTask.periodicity,
                          selectedTask.schedule_hour,
                          selectedTask.schedule_day_of_week,
                          newDayOfMonth
                        )
                        const nextExecutionAt = nextExecution ? nextExecution.toISOString() : null

                        setSelectedTask({
                          ...selectedTask,
                          schedule_day_of_month: newDayOfMonth,
                          next_execution_at: nextExecutionAt,
                        })
                      }}
                    >
                      <option value={1}>First day</option>
                      <option value={-1}>Last day</option>
                    </select>
                  </>
                )}

                {/* Time selection for scheduled periodicities (not after_workout) */}
                {selectedTask.periodicity !== 'after_workout' && (
                  <>
                    <span className="field-label">at</span>
                    <select
                      value={selectedTask.schedule_hour || 9}
                      onChange={(e) => {
                        const newHour = Number(e.target.value)
                        const nextExecution = calculateNextExecution(
                          selectedTask.periodicity,
                          newHour,
                          selectedTask.schedule_day_of_week,
                          selectedTask.schedule_day_of_month
                        )
                        const nextExecutionAt = nextExecution ? nextExecution.toISOString() : null

                        setSelectedTask({
                          ...selectedTask,
                          schedule_hour: newHour,
                          next_execution_at: nextExecutionAt,
                        })
                      }}
                    >
                      {Array.from({ length: 24 }, (_, i) => (
                        <option key={`h_${i.toString()}`} value={i}>
                          {formatTimeInUserTimezone(i)}
                        </option>
                      ))}
                    </select>
                    <span className="field-label" style={{ fontSize: '0.9em', color: 'var(--color-text-secondary)' }}>
                      ({getTimezoneOffset()})
                    </span>
                  </>
                )}
              </InlineFieldGroup>
            </FormGroup>

            {selectedTask.id !== 'new' && (
              <div style={{ display: 'flex', flexDirection: 'row', gap: '10px' }}>
                <FormGroup style={{ flex: 1 }}>
                  <label htmlFor="last_executed_at">Last Execution</label>
                  <input
                    id="last_executed_at"
                    className="execution-date"
                    readOnly={true}
                    disabled={true}
                    value={formatLastExecution(selectedTask.last_executed_at)}
                  />
                </FormGroup>

                {selectedTask.next_execution_at && (
                  <FormGroup style={{ flex: 1 }}>
                    <label htmlFor="next_execution_at">Next Execution</label>
                    <input
                      id="next_execution_at"
                      className="execution-date"
                      readOnly={true}
                      disabled={true}
                      value={formatNextExecution(selectedTask.next_execution_at)}
                    />
                  </FormGroup>
                )}
              </div>
            )}

            <FormGroup>
              <label htmlFor="prompt">Instructions *</label>
              <textarea
                value={selectedTask.prompt}
                name="prompt"
                onChange={(e) => setSelectedTask({ ...selectedTask, prompt: e.target.value })}
                placeholder="What would you like the AI to do regularly?"
              />
            </FormGroup>

            <FormGroup>
              <label htmlFor="target_athlete_ids">For these Athletes</label>
              {
                <AthleteSelectionContainer>
                  {athletes.length === 0 ? (
                    <div style={{ color: 'var(--color-text-secondary)', fontSize: '14px' }}>No athletes connected</div>
                  ) : (
                    athletes.map((athlete) => (
                      <AthleteCheckbox key={athlete.id}>
                        <CheckboxGroup>
                          <input
                            type="checkbox"
                            id={athlete.id}
                            checked={(selectedTask.target_athlete_ids || []).includes(athlete.id)}
                            onChange={(e) => {
                              const currentIds = selectedTask.target_athlete_ids || []
                              if (e.target.checked) {
                                setSelectedTask({
                                  ...selectedTask,
                                  target_athlete_ids: [...currentIds, athlete.id],
                                })
                              } else {
                                setSelectedTask({
                                  ...selectedTask,
                                  target_athlete_ids: currentIds.filter((id) => id !== athlete.id),
                                })
                              }
                            }}
                          />
                          <label htmlFor={athlete.id}>{athlete.name}</label>
                        </CheckboxGroup>
                        {/*}
                        {selectedTask.id !== 'new' && (
                          <button
                            type="button"
                            className="execute-button"
                            onClick={() => executeTaskNow(selectedTask.id, athlete.id)}
                            disabled={isLoading || selectedTask.periodicity === 'after_workout'}
                            title={
                              selectedTask.periodicity === 'after_workout'
                                ? 'Cannot execute manually - runs after workout upload'
                                : `Execute for ${athlete.name}`
                            }
                          >
                            <Play size={14} />
                          </button>
                        )}*/}
                      </AthleteCheckbox>
                    ))
                  )}
                </AthleteSelectionContainer>
              }
            </FormGroup>

            <FormGroup>
              <label htmlFor="email_results">Perform these actions with the results</label>
              <CheckboxGroup>
                <input
                  type="checkbox"
                  id="email_results"
                  checked={selectedTask.email_results ?? false}
                  onChange={(e) => setSelectedTask({ ...selectedTask, email_results: e.target.checked })}
                />
                <label htmlFor="email_results">Send results by email</label>
                {selectedTask.email_results && (
                  <InlineFieldGroup style={{ marginTop: '8px', marginLeft: '8px' }}>
                    <select
                      style={{ fontSize: 14 }}
                      value={selectedTask.email_mode || 'always'}
                      name="email_mode"
                      onChange={(e) => {
                        setSelectedTask({
                          ...selectedTask,
                          email_mode: e.target.value as 'always' | 'tool',
                        })
                      }}
                    >
                      <option value="always">Always</option>
                      <option value="tool">If requested by the agent</option>
                    </select>
                  </InlineFieldGroup>
                )}
              </CheckboxGroup>
            </FormGroup>

            <ButtonGroup>
              {selectedTask.id !== 'new' && (
                <Button variant="danger" onClick={() => deleteTask(selectedTask.id)} disabled={isLoading}>
                  Delete Automation
                </Button>
              )}

              <div style={{ flex: 1 }} />

              {selectedTask.id !== 'new' && (
                <Button
                  variant="secondary"
                  className="execute-button"
                  onClick={() => executeTaskNow(selectedTask, null)}
                  disabled={isExecuting}
                  title={
                    selectedTask.periodicity === 'after_workout'
                      ? 'Cannot execute manually - runs after workout upload'
                      : 'Execute for all athletes'
                  }
                >
                  {isExecuting ? (
                    <>
                      <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                      Executing...
                    </>
                  ) : (
                    <>
                      <Play size={16} color={theme.colors.primary} />
                      Execute Now
                    </>
                  )}
                </Button>
              )}

              <Button
                variant="primary"
                onClick={saveTask}
                disabled={
                  !selectedTask.title ||
                  !selectedTask.prompt ||
                  isLoading ||
                  (selectedTask.id !== 'new' && !isTaskDirty())
                }
              >
                {isLoading ? 'Saving...' : selectedTask.id === 'new' ? 'Create Automation' : 'Save Automation'}
              </Button>
            </ButtonGroup>
          </DetailScroll>
        ) : (
          <EmptyState>
            <p>Select an automation to view details or create a new automation.</p>
            <p>
              You can also automate an analysis inside a conversation, by asking the AI to execute the analysis
              regularly.
            </p>
            <p>e.g. "Execute this analysis every Monday morning at 9am."</p>
          </EmptyState>
        )}
      </DetailPanel>

      <TaskExecutionResultsModal
        isOpen={isResultsModalOpen}
        onClose={() => setIsResultsModalOpen(false)}
        results={executionResults}
      />

      <Notification $show={showNotification}>
        {notificationMessage}
      </Notification>
    </Container>
  )
}
